import type { UnpublishableWarningType } from "@v1/components/header/unpublishableWarnings";

import * as React from "react";

import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import { getUnpublishableWarnings } from "@v1/components/header/unpublishableWarnings";
import { useElementName } from "@v1/hooks/element/useElementName";
import { useDraftElementMetadata } from "@v1/hooks/useDraftElementMetadata";
import usePublishOnClickInfo from "@v1/hooks/usePublishingInfo";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { useStoreProductsFromDraftElement } from "@v1/hooks/useStoreProducts";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import { selectUnpublishableComponentIssues } from "@v1/reducers/core-reducer";
import { selectIsShopifyStoreClosed } from "@v1/reducers/ui-reducer";
import { useEditorSelector, useEditorStore } from "@v1/store/store";
import { MAX_SECTION_NAME_LENGTH } from "@v1/utils/element";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { docs } from "@v2/utils/docs";
import { getProductLabels } from "@v2/utils/project-labels";
import { isEmpty } from "replo-utils/lib/misc";

type PublishPageButtonProps = {
  isPreviewBeforePublishView: boolean;
};

export const PublishPageButton: React.FC<PublishPageButtonProps> = ({
  isPreviewBeforePublishView,
}) => {
  const reduxStore = useEditorStore();
  const [productTemplateConfirmationModalOpen, setConfirmationModalOpen] =
    React.useState(false);
  const [warningsModalOpen, setWarningsModalOpen] = React.useState(false);
  const [renameModalOpen, setRenameModalOpen] = React.useState(false);
  const [renameSectionName, setRenameSectionName] = React.useState("");
  const [isRenameSaving, setIsRenameSaving] = React.useState(false);
  const isStoreClosed = useEditorSelector(selectIsShopifyStoreClosed);
  const errorToast = useV1ErrorToast();
  const logEvent = useLogAnalytics();
  const setDraftElement = useSetDraftElement();
  const { products } = useStoreProductsFromDraftElement();
  const elementMetadata = useDraftElementMetadata();
  const { elementName, handleUpdateElementName } = useElementName({
    element: elementMetadata,
    initialName: elementMetadata?.name,
  });

  const onSelectComponentId = (componentId: string) => {
    setDraftElement({ componentIds: [componentId] });
    setWarningsModalOpen(false);
  };

  const {
    publishNotAvailableReason,
    draftElementType,
    numAssignedProducts,
    publish,
    isPublishing,
    pageEditorData,
  } = usePublishOnClickInfo();

  const publishOnClick = () => {
    if (!elementMetadata) {
      return;
    }

    if (publishNotAvailableReason) {
      errorToast(
        publishNotAvailableReason.title,
        publishNotAvailableReason.message,
        "error.publish.unavailable",
        {
          publishNotAvailableReasonTitle: publishNotAvailableReason.title,
          publishNotAvailableReasonMessage: publishNotAvailableReason.message,
        },
      );
      return;
    }

    const componentUnpublishableIssues = selectUnpublishableComponentIssues(
      reduxStore.getState(),
      products,
    );

    if (!isEmpty(componentUnpublishableIssues)) {
      setWarningsModalOpen(true);
    } else if (
      draftElementType === "shopifyProductTemplate" &&
      numAssignedProducts > 0
    ) {
      setConfirmationModalOpen(true);
    } else if (
      elementMetadata.type === "shopifySection" &&
      !elementMetadata.publishedAt
    ) {
      setRenameModalOpen(true);
    } else {
      publish();
    }
  };

  const handleRenameAndPublish = async (newName: string) => {
    if (elementName !== newName) {
      await handleUpdateElementName(newName);
    }
    publish();
    setRenameModalOpen(false);
  };

  const handleSubmitRename = async () => {
    setIsRenameSaving(true);
    try {
      await handleRenameAndPublish(renameSectionName);
    } finally {
      setIsRenameSaving(false);
    }
  };

  React.useEffect(() => {
    if (elementName) {
      setRenameSectionName(elementName);
    }
  }, [elementName]);

  return (
    <>
      <div className="flex flex-1">
        <DeprecatedButton
          layoutClassName="w-full"
          onClick={() => {
            if (isPreviewBeforePublishView) {
              logEvent("header.publish", {
                buttonLocation: "preview",
              });
            }
            publishOnClick();
          }}
          /**
           * Note (Noah, 2022-12-18): We specifically don't want the button to show
           * as disabled while an update to the element is saving, because it's really
           * distracting if it changes between blue and gray all the time when you're
           * editing the page. Instead, we bail out early in onClick if that's the case
           */
          disabled={isPublishing || isStoreClosed}
          variant={isPublishing ? "secondary" : "primary"}
          size="base"
          tooltipText={`Publish ${pageEditorData.singularDisplayName} to Shopify`}
          isLoading={isPublishing}
          id="publish-button"
          data-testid="publish-button"
        >
          {`Publish ${pageEditorData.singularDisplayName}`}
        </DeprecatedButton>
      </div>
      <Modal
        isOpen={productTemplateConfirmationModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmationModalOpen(false);
          }
        }}
        title="Publish Product Template?"
        size="sm"
        description={`After publishing, this Product Template will apply to the ${numAssignedProducts > 1 ? numAssignedProducts : ""} assigned product${numAssignedProducts > 1 ? "s" : ""} on your live store.`}
        footer={
          <div className="flex flex-row gap-2">
            <DeprecatedButton
              variant="secondary"
              size="base"
              type="button"
              onClick={() => setConfirmationModalOpen(false)}
            >
              Cancel
            </DeprecatedButton>
            <DeprecatedButton
              variant="primary"
              size="base"
              type="button"
              onClick={() => {
                publish();
                setConfirmationModalOpen(false);
              }}
            >
              Publish Product Template
            </DeprecatedButton>
          </div>
        }
      />
      <Modal
        isOpen={warningsModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setWarningsModalOpen(false);
          }
        }}
        title="Publishing Errors"
        size="base"
        footer={
          <DeprecatedButton
            variant="primary"
            size="base"
            type="button"
            onClick={() => {
              publish();
              setWarningsModalOpen(false);
            }}
          >
            Publish Anyway
          </DeprecatedButton>
        }
      >
        <UnpublishableComponentsWarningContent
          onSelectComponentId={onSelectComponentId}
        />
      </Modal>
      <Modal
        isOpen={renameModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRenameModalOpen(false);
          }
        }}
        title="Publish with this name?"
        size="sm"
        footer={
          <DeprecatedButton
            variant="primary"
            size="base"
            onClick={() => void handleSubmitRename()}
            isLoading={isRenameSaving}
            disabled={renameSectionName.length > MAX_SECTION_NAME_LENGTH}
          >
            Save and Publish
          </DeprecatedButton>
        }
      >
        <RenameSectionModalContent
          sectionName={renameSectionName}
          setSectionName={setRenameSectionName}
        />
      </Modal>
    </>
  );
};

const UnpublishableComponentsWarningContent = ({
  onSelectComponentId,
}: {
  onSelectComponentId: (componentId: string) => void;
}) => {
  const { products } = useStoreProductsFromDraftElement();
  const componentUnpublishableIssues = useEditorSelector((state) => {
    return selectUnpublishableComponentIssues(state, products);
  });
  const processedIssueTypes = new Set();
  const unpublishableWarnings = getUnpublishableWarnings();
  return (
    <>
      {componentUnpublishableIssues.flatMap(
        (componentUnpublishableIssue, i) => {
          return componentUnpublishableIssue.issues.map((issue) => {
            if (processedIssueTypes.has(issue.type)) {
              return null;
            }
            processedIssueTypes.add(issue.type);
            const issueData =
              unpublishableWarnings[issue.type as UnpublishableWarningType];
            return (
              <div key={issue.type}>
                {i > 0 && <hr className="bg-border my-2 h-px border-0" />}
                <h2 className="text-base font-medium">{issueData?.title}</h2>
                <p className="mb-4">{issueData?.text}</p>
                <ul className="mb-4 list-inside list-disc">
                  {componentUnpublishableIssues
                    .filter((component) => {
                      return component.issues.some(
                        (iss) => iss.type === issue.type,
                      );
                    })
                    .map(({ componentId, componentData }) => (
                      <li key={componentId} className="mt-2 pl-4">
                        <button
                          onClick={() => onSelectComponentId(componentId)}
                          // TODO: replace this button with a DS button
                          // eslint-disable-next-line replo/no-tailwind-literal-color
                          className="text-primary underline hover:text-blue-800"
                        >
                          {componentData.label}
                        </button>
                      </li>
                    ))}
                </ul>
                {issueData?.solution && (
                  <p>
                    <b>Solution: </b>
                    {issueData?.solution}
                  </p>
                )}
              </div>
            );
          });
        },
      )}
    </>
  );
};

const RenameSectionModalContent = ({
  sectionName,
  setSectionName,
}: {
  sectionName: string;
  setSectionName: (name: string) => void;
}) => {
  const productLabels = getProductLabels();

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">
        This section's name will show in the {productLabels.shopifyThemeBuilder}
        . You will not be able to rename this section after you publish it.{" "}
        <a
          href={docs.warnings.cannotRenameSection}
          target="_blank"
          className="underline"
          rel="noreferrer"
        >
          Learn More
        </a>
      </p>

      <DeprecatedLabeledControl
        label="Section Name"
        id="save-section-name"
        error={
          sectionName.length > MAX_SECTION_NAME_LENGTH
            ? `Section name must be less than ${MAX_SECTION_NAME_LENGTH} characters`
            : undefined
        }
      >
        <DeprecatedInput
          size="base"
          autoFocus
          value={sectionName}
          onChange={(e) => setSectionName(e.target.value)}
          placeholder="Enter section name..."
        />
      </DeprecatedLabeledControl>
    </div>
  );
};
